import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {DragDropContext, Droppable} from "react-beautiful-dnd";

// Components
import Modal from "../../components/Modal.js";
import Accordion from "../../components/Accordion.js";
import RenderChecksheetBuilder from "./RenderChecksheetBuilder.js";

// Hooks
import useApi from "../../hooks/useApi.js";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Utils
import {prettyDateWithDayInUserTimezone} from "../../utils/helpers.js";

// Style
import {
  HeadingCenter,
  Button,
  NotLoaded,
  Loader,
  Text,
  Inline,
  HeadingSmall
} from "../../style/components/general.js";
import {border, pad, radius} from "../../style/components/variables.js";

const ModalChecksheetHistory = ({visible, setVisible, checksheetId, stateId, publishedInfo}) => {
  const {api} = useApi("checksheet-versions");

  const {settings} = useContext(SettingsContext);
  const {facility} = useContext(FacilityNavContext) || {};

  const [versions, setVersions] = useState();
  const [target, setTarget] = useState();
  const [viewMode, setViewMode] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const limit = useRef();
  const scrollContainer = useRef();

  const getVersions = useCallback(
    (p = 1) => {
      setLoading(true);
      api
        .callGet(null, {
          facilityChecksheetId: checksheetId,
          stateId,
          page: p,
          limit: limit.current
        })
        .then(({data, status}) => {
          if (status === 200) {
            setVersions(prev => (prev ? [...prev, ...data.versions] : data.versions));
            setPageTotal(data.pages);
            setCurrentPage(data.page);
          } else if (versions === undefined) setVersions([]);

          setLoading(false);
        });
    },
    [api, checksheetId, versions, stateId]
  );

  useEffect(() => {
    if (!limit.current) {
      const approxRows = Math.ceil((window.innerHeight * 0.9) / 50);

      limit.current = approxRows;

      getVersions();
    }
  }, [api, getVersions]);

  // Load more on Scroll
  const handleScroll = useCallback(() => {
    if (scrollContainer?.current?.scrollHeight) {
      const maxScrollTop =
        scrollContainer.current.scrollHeight - scrollContainer.current.clientHeight;
      if (
        scrollContainer.current.scrollTop + 1 >= maxScrollTop &&
        currentPage < pageTotal &&
        !loading
      )
        getVersions(currentPage + 1);
    }
  }, [currentPage, getVersions, loading, pageTotal]);

  useEffect(() => {
    const modal = scrollContainer.current;
    modal?.addEventListener("scroll", handleScroll);
    return () => modal?.removeEventListener("scroll", handleScroll);
  }, [handleScroll, target]);

  const getLabel = version => {
    const {modification, builder, user, createdAt, bulk} = version;

    const formattedDate = prettyDateWithDayInUserTimezone(createdAt, settings.timezone);
    const formattedUser = `${user.firstName} ${user.lastName}`;

    const prefix = `${formattedDate} - ${formattedUser}`;
    const fallback = `${formattedDate} - ${formattedUser} edited checksheet`;

    if (bulk) return fallback;

    const [addedGroup] = Object.keys(modification).filter(
      key => modification[key] === "added" && builder.byId[key]?.element === "group"
    );

    const [removedGroup] = Object.keys(modification).filter(
      key => modification[key]?.element === "group" && modification[key].operation === "removed"
    );

    const [editedGroup] = Object.keys(modification).filter(
      key => builder.byId[key]?.element === "group"
    );

    const [addedComponent] = Object.keys(modification).filter(
      key => modification[key] === "added" && builder.byId[key]?.element === "component"
    );

    const [removedComponent] = Object.keys(modification).filter(
      key => modification[key].element === "component" && modification[key].operation === "removed"
    );

    const [editedComponent] = Object.keys(modification).filter(
      key => builder.byId[key]?.element === "component"
    );

    const [appliedTemplate] = Object.keys(modification).filter(
      key => modification[key]?.appliedTemplate
    );

    const [addedField] = Object.keys(modification).filter(
      key => builder.byId[key]?.element === "field" && modification[key] === "added"
    );

    const [removedField] = Object.keys(modification).filter(
      key => modification[key].element === "field" && modification[key].operation === "removed"
    );

    const [editedField] = Object.keys(modification).filter(
      key => builder.byId[key]?.element === "field"
    );

    const getLabelHelper = (id, item) => {
      if (item?.label) return item.label.toUpperCase();
      if (facility?.builder?.byId?.[id]?.label)
        return facility.builder.byId[id].label.toUpperCase();
      return "";
    };

    if (addedGroup) {
      const item = builder.byId[addedGroup];

      if (item) return `${prefix} added group ${getLabelHelper(addedGroup, item)}`;

      return fallback;
    }

    if (removedGroup) {
      const item = modification[removedGroup];

      if (item) return `${prefix} removed group ${getLabelHelper(removedGroup, item)}`;

      return fallback;
    }

    if (editedGroup) {
      const item = builder.byId[editedGroup];

      if (item) return `${prefix} updated info for group ${getLabelHelper(editedGroup, item)}`;

      return fallback;
    }

    if (addedComponent) {
      const item = builder.byId[addedComponent];

      if (item) return `${prefix} added component ${getLabelHelper(addedComponent, item)}`;

      return fallback;
    }

    if (removedComponent) {
      const item = modification[removedComponent];

      if (item) return `${prefix} removed component ${getLabelHelper(removedComponent, item)}`;

      return fallback;
    }

    if (appliedTemplate) {
      const item = builder.byId[appliedTemplate];

      if (item) return `${prefix} applied template to ${getLabelHelper(appliedTemplate, item)}`;

      return fallback;
    }

    if (editedComponent) {
      const item = builder.byId[editedComponent];

      if (item)
        return `${prefix} updated info for component ${getLabelHelper(editedComponent, item)}`;

      return fallback;
    }

    if (addedField && removedField) {
      const item = builder.byId[addedField];

      if (item) return `${prefix} updated field ${getLabelHelper(addedField, item)}`;

      return fallback;
    }

    if (addedField) {
      const item = builder.byId[addedField];

      if (item) return `${prefix} added field ${getLabelHelper(addedField, item)}`;

      return fallback;
    }

    if (removedField) {
      const item = modification[removedField];

      if (item) return `${prefix} removed field ${getLabelHelper(removedField, item)}`;

      return fallback;
    }

    if (editedField) {
      const item = builder.byId[editedField];

      if (item) return `${prefix} updated field ${getLabelHelper(editedField, item)}`;

      return fallback;
    }

    return fallback;
  };

  if (target)
    return (
      <Modal
        visible={visible && !!target}
        setVisible={setVisible}
        hasBackButton
        goBack={() => {
          setTarget(undefined);
          setViewMode(undefined);
        }}>
        <ModalTitle>
          {prettyDateWithDayInUserTimezone(target.createdAt, settings.timezone)}
        </ModalTitle>
        <HeadingSmall center>
          Edited by {target.user.firstName} {target.user.lastName}
        </HeadingSmall>
        <Explanation center>
          <Text quiet>
            <strong>Explanation: </strong>
            {target.note}
          </Text>
        </Explanation>
        <br />
        <Button
          onClick={() =>
            setViewMode(prev => (prev === "modification" ? "builder" : "modification"))
          }>
          {viewMode === "modification" ? "View Builder" : "View Modification"}
        </Button>
        <br />
        <Builder>
          <DragDropContext onDragStart={() => {}} onDragEnd={() => {}}>
            <Droppable droppableId="version.builder" isDropDisabled>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <RenderChecksheetBuilder
                    builder={target.builder}
                    readOnly
                    inChecksheetHistory
                    modifications={viewMode === "modification" ? target.modification : undefined}>
                    {provided.placeholder}
                  </RenderChecksheetBuilder>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Builder>
      </Modal>
    );

  return (
    <Modal ref={scrollContainer} visible={visible} setVisible={setVisible}>
      <ModalTitle>Builder History</ModalTitle>
      {publishedInfo?.publishedAt && (
        <ModalSubHeading center>
          Published{" "}
          {publishedInfo.publishedBy
            ? `by ${publishedInfo.publishedBy?.firstName} ${publishedInfo.publishedBy?.lastName} `
            : ""}
          on {prettyDateWithDayInUserTimezone(publishedInfo.publishedAt, settings?.timezone)}
        </ModalSubHeading>
      )}
      {versions?.map(version => (
        <Accordion key={`version-${version.id}`} label={getLabel(version)} wrap>
          <>
            <Explanation>
              <Text quiet>
                <strong>Explanation:</strong> {version.note}
              </Text>
            </Explanation>

            <AccordionContent>
              <Button
                type="button"
                onClick={() => {
                  setTarget(version);
                  setViewMode("builder");
                }}>
                View Builder
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setTarget(version);
                  setViewMode("modification");
                }}>
                View Modification
              </Button>
            </AccordionContent>
          </>
        </Accordion>
      ))}
      {versions?.length === 0 && (
        <Centered>
          <Text>No versions found</Text>
        </Centered>
      )}
      {loading && (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}
    </Modal>
  );
};

ModalChecksheetHistory.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  checksheetId: PropTypes.string.isRequired,
  stateId: PropTypes.string,
  publishedInfo: PropTypes.objectOf(PropTypes.any)
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin-bottom: ${pad}px;
`;

const ModalSubHeading = styled(HeadingSmall)`
  margin-bottom: ${pad * 2}px;
`;

const AccordionContent = styled(Inline)`
  margin: 0 0 ${pad / 2}px ${pad * 2}px;
  border: ${border} solid ${({theme}) => theme.tertiary};
  gap: ${pad}px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Explanation = styled.div`
  margin: ${pad / 2}px 0 ${pad / 2}px ${pad * 2}px;

  ${({center}) =>
    center &&
    css`
      margin: ${pad / 2}px 0;
      p {
        text-align: center;
      }
    `}
`;

const Centered = styled(Inline)`
  justify-content: center;
  width: 100%;
`;

const Builder = styled.div`
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
`;

export default ModalChecksheetHistory;
